import { useSocketContext } from "@/context/socket.context/socket.context";
import { TLiveAgent, TLiveAgents } from "@/models/call.center.model";
import { getLiveAgents } from "@/services/call-center/agents.service";
import { CUSTOM_EVENT_USER_STATUS_CHANGED } from "@icebergeradmin/common";
import { useEffect, useState } from "react";

export const useLiveAgents = (callCenterId?: number) => {
  const socket = useSocketContext();
  const [liveAgents, setLiveAgents] = useState<TLiveAgents>();
  const [loading, setLoading] = useState(false);

  const updateLiveAgentsOnUserStatusChange = (agent: TLiveAgent) => {
    setLiveAgents((prev) => {
      if (!prev) return prev;
      const agentInArray = prev.agents.some((i) => i.id === agent.id);

      if (agentInArray) {
        return {
          total: prev.total,
          agents: prev.agents.map((i) => (i.id === agent.id ? agent : i)),
        };
      } else {
        return {
          total: prev.total + 1,
          agents: [agent, ...prev.agents],
        };
      }
    });
  };

  const loadLiveAgents = async () => {
    setLoading(true);
    try {
      const response = await getLiveAgents(callCenterId);
      setLiveAgents(response);
    } catch {
      // Error loading live agents
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!callCenterId) return;

    // Named handler so cleanup removes THIS listener only. The single-arg
    // form `socket.off(event)` wipes every listener for the event — would
    // silence other hooks that may listen to USER_STATUS_CHANGED.
    const onStatusChanged = (agent: TLiveAgent) => {
      updateLiveAgentsOnUserStatusChange(agent);
    };
    socket.on(CUSTOM_EVENT_USER_STATUS_CHANGED, onStatusChanged);

    loadLiveAgents();

    return () => {
      socket.off(CUSTOM_EVENT_USER_STATUS_CHANGED, onStatusChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, callCenterId]);

  return {
    total: liveAgents?.total || 0,
    liveAgents: liveAgents?.agents,
    loading,
  };
};
